import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { SIGN_DIRECTION, SignedNumberField } from '../../../../components/inputs/SignedNumberField';
import type { SignDirection } from '../../../../components/inputs/SignedNumberField';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { inventoryAuditsApi } from '../inventoryAuditApi';
import type { InventoryAuditDetail, InventoryAuditLineDetail } from '../inventoryAuditTypes';
import { INVENTORY_AUDIT_VARIANCE_REASONS } from '../inventoryAuditTypes';
import type { StockAdjustmentDirection } from '../stockTypes';

/**
 * Record what was physically found for ONE item on the count sheet.
 *
 * <b>Nothing here moves stock, and the form says so where the reader is about to act.</b> The
 * adjustment is a recorded correction — what the count says the shelf ought to be moved by — and
 * applying it is a stock adjustment raised separately under its own key, with its own reason. A
 * count sheet that silently corrected the shelf would be the drift `StockLedger` exists to
 * prevent, arriving through a form.
 *
 * <b>The variance is DERIVED and never typed.</b> It is the count minus the baseline, so a field
 * for it would be one fact in two slots with nothing keeping them in step.
 */
export function RecordCountModal({
  open,
  auditId,
  line,
  onClose,
  onSaved,
}: {
  open: boolean;
  auditId: string;
  line: InventoryAuditLineDetail;
  onClose: () => void;
  onSaved: (audit: InventoryAuditDetail) => void;
}) {
  /*
    WITHHELD without `personnel.read` rather than drawn and refused — hide, don't disable. A
    reader who lacks it records a count with nobody named, which is a real state: the field is
    optional. `inventory-audits` implies `personnel.read` SCOPED to record-count for exactly this
    picker, so a holder of the count key has it and a mere reader of the register does not.
  */
  const canReadPersonnel = usePermission('personnel.read');

  /*
    PER-STORE when the audit covers more than one, which is the source's own rule: the line total
    becomes the SUM of the stores, so the combined figure can never drift from the detail that
    explains it — and a variance stays traceable to the store it happened in. One store means one
    box, because a breakdown of one is a second question with one answer.
  */
  const perStore = line.facilityBreakdown.length > 1;

  const [actual, setActual] = useState('');
  const [byStore, setByStore] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [accountable, setAccountable] = useState('');
  const [direction, setDirection] = useState<SignDirection | null>(null);
  const [magnitude, setMagnitude] = useState('');
  const [adjustmentTouched, setAdjustmentTouched] = useState(false);

  const [staff, setStaff] = useState<{ value: string; label: string }[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    setActual(line.actualQuantity == null ? '' : String(line.actualQuantity));
    setByStore(
      Object.fromEntries(
        line.facilityBreakdown.map((f) => [
          f.storageFacilityId,
          f.actualQuantity == null ? '' : String(f.actualQuantity),
        ]),
      ),
    );
    setReason(line.varianceReason ?? '');
    setAccountable(line.accountablePersonnelId ?? '');

    // Seeded from the SIGNED stored value, split back into the pair the form asks in.
    setDirection(
      line.adjustmentQuantity == null
        ? null
        : line.adjustmentQuantity < 0
          ? SIGN_DIRECTION.deduct
          : SIGN_DIRECTION.add,
    );
    setMagnitude(
      line.adjustmentQuantity == null ? '' : String(Math.abs(line.adjustmentQuantity)),
    );

    setAdjustmentTouched(false);
    setConfirmed(false);
    setError(null);
  }, [open, line]);

  useEffect(() => {
    if (!open || !canReadPersonnel) return;

    const controller = new AbortController();

    personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (page) => {
        const names = await personsApi.resolve(
          // A record above this reader's access level arrives with NO personId — that absence
          // is the mask — so there is nothing to resolve. The option falls back to the employee
          // number below, which identifies the row without naming the person.
          page.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          page.items.map((p) => ({
            value: p.id,
            label: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
          })),
        );
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [open, canReadPersonnel]);

  /*
    The COUNTED total, derived rather than held in state — a stored copy would be one render
    behind whatever the reader just typed. Null while NOTHING has been counted, so an untouched
    line stays "not counted" rather than reading as a genuine zero, which is the distinction the
    whole sheet's progress tally rests on.
  */
  const counted = useMemo<number | null>(() => {
    if (!perStore) {
      const trimmed = actual.trim();
      if (trimmed === '') return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }

    const entered = Object.values(byStore).filter((v) => v.trim() !== '');
    if (entered.length === 0) return null;

    return entered.reduce((sum, v) => sum + (Number.isFinite(Number(v)) ? Number(v) : 0), 0);
  }, [perStore, actual, byStore]);

  const variance = counted == null ? null : counted - line.snapshotQuantity;

  /*
    The adjustment DEFAULTS to the variance until the reader overrides it, which is the source's
    own behaviour: the correction a count calls for is, in the ordinary case, exactly the
    difference it found. Derived during render rather than pushed into state by an effect, so it
    follows the count as it is typed instead of trailing it by a paint.
  */
  const effectiveDirection = adjustmentTouched
    ? direction
    : variance == null || variance === 0
      ? null
      : variance < 0
        ? SIGN_DIRECTION.deduct
        : SIGN_DIRECTION.add;

  const effectiveMagnitude = adjustmentTouched
    ? magnitude
    : variance == null || variance === 0
      ? ''
      : String(Math.abs(variance));

  const countValid =
    counted == null || (Number.isInteger(counted) && counted >= 0);

  // BOTH halves or neither — the server refuses one without the other, so the form says so first.
  const adjustmentValid =
    (effectiveDirection == null && effectiveMagnitude.trim() === '') ||
    (effectiveDirection != null && Number(effectiveMagnitude) >= 1);

  const canSave = countValid && adjustmentValid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(
        await inventoryAuditsApi.recordCount(auditId, line.id, {
          // Ignored by the server when the per-store counts are sent — the sum is authoritative.
          actualQuantity: perStore ? null : counted,
          varianceReason: reason.trim() || null,
          accountablePersonnelId: accountable || null,
          adjustmentDirection:
            effectiveDirection == null
              ? null
              : (effectiveDirection as unknown as StockAdjustmentDirection),
          adjustmentQuantity:
            effectiveMagnitude.trim() === '' ? null : Number(effectiveMagnitude),
          facilities: perStore
            ? line.facilityBreakdown.map((f) => ({
                storageFacilityId: f.storageFacilityId,
                actualQuantity:
                  byStore[f.storageFacilityId]?.trim() === '' ||
                  byStore[f.storageFacilityId] === undefined
                    ? null
                    : Number(byStore[f.storageFacilityId]),
              }))
            : null,
        }),
      );
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not record the count.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'record-count' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record count"
      size="lg"
      id="record-count-modal"
      confirm={{
        id: 'record-count-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="record-count-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="record-count-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The item is NOT asked for — the reader chose it by pressing its row — and neither is the
          baseline, which is a fact the sheet already holds. Read-only facts are the Table Display
          View; the one thing being ANSWERED is the count, which is a field.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Item', value: line.itemName },
            { label: 'SKU', value: line.itemSku, mono: true },
            { label: 'Category', value: line.inventoryCategoryName },
            { label: 'Unit', value: line.unitOfMeasureName },
            {
              label: 'Current (system)',
              value: line.snapshotQuantity,
              mono: true,
            },
          ]}
        />

        {perStore ? (
          <div className="flex flex-col gap-3">
            {line.facilityBreakdown.map((store) => (
              <TextField
                key={store.storageFacilityId}
                id={`record-count-facilities-${store.storageFacilityId}`}
                label={store.storageFacilityName}
                description={`The system believes this store holds ${store.snapshotQuantity}. Leave it empty if it has not been counted.`}
                type="number"
                min={0}
                inputMode="numeric"
                className="font-mono tabular-nums"
                value={byStore[store.storageFacilityId] ?? ''}
                onChange={(e) =>
                  setByStore((current) => ({
                    ...current,
                    [store.storageFacilityId]: e.target.value,
                  }))
                }
                disabled={saving}
              />
            ))}
          </div>
        ) : (
          <TextField
            id="record-count-actual-quantity"
            label="Actual (counted)"
            description="What was physically found. Zero is a finding; leave it empty if this row has not been counted yet."
            type="number"
            min={0}
            inputMode="numeric"
            className="font-mono tabular-nums"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
            disabled={saving}
            error={error?.fieldError('actualQuantity')}
          />
        )}

        {/*
          DERIVED, and shown as a fact rather than collected. It is the count minus the baseline,
          so a field for it would be one value in two slots. An em dash until something has been
          counted — a variance of 0 is a finding, and "not counted" is not.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            {
              label: 'Variance',
              value: variance == null ? null : variance > 0 ? `+${variance}` : String(variance),
              mono: true,
            },
            ...(perStore && counted != null
              ? [{ label: 'Counted total', value: counted, mono: true }]
              : []),
          ]}
        />

        {/*
          SUGGESTIONS with a free-typed fallback — `allowCustom` and no create handler, because
          there is no catalog behind a reason and nothing to create. The aggregation test: nothing
          totals by this today, so it is prose for a person to read.
        */}
        <Select
          id="record-count-variance-reason"
          label="Reason for variance"
          description="Pick a common reason or type your own — any wording is accepted. Nothing groups by it, so it is read rather than counted."
          value={reason}
          onChange={setReason}
          options={INVENTORY_AUDIT_VARIANCE_REASONS.map((r) => ({ value: r, label: r }))}
          allowCustom
          placeholder="Select a reason or type your own"
          disabled={saving}
          error={error?.fieldError('varianceReason')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Withheld entirely without the key — see the note at the top of this component. */}
          {canReadPersonnel ? (
            <Select
              id="record-count-accountable-personnel-id"
              label="Accountable person"
              description="Who answers for the variance. Optional — a counting error is nobody's."
              value={accountable}
              onChange={setAccountable}
              options={staff}
              remote
              placeholder="Search staff"
              disabled={saving}
              error={error?.fieldError('accountablePersonnelId')}
            />
          ) : null}

          {/*
            A DIRECTION and a MAGNITUDE, never a signed number — see `<SignedNumberField>`, where
            the call is argued. The description states the consequence the whole module rests on.
          */}
          <SignedNumberField
            id="record-count-adjustment-quantity"
            label="Adjustment"
            description="Defaults to the variance. RECORDED ONLY — no stock moves and no ledger line is written. Correcting the shelf is a stock adjustment, raised separately."
            direction={effectiveDirection}
            onDirectionChange={(next) => {
              setAdjustmentTouched(true);
              setDirection(next);
            }}
            value={effectiveMagnitude}
            onValueChange={(next) => {
              setAdjustmentTouched(true);
              setMagnitude(next);
            }}
            min={1}
            disabled={saving}
            error={
              adjustmentValid
                ? error?.fieldError('adjustmentQuantity') ??
                  error?.fieldError('adjustmentDirection')
                : effectiveDirection == null
                  ? 'Choose whether the adjustment adds or deducts.'
                  : 'Enter how much the adjustment moves, or clear the direction.'
            }
          />
        </div>
      </div>
    </Modal>
  );
}
